import * as React from "react";
import { Box, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Skill, SkillCategory, SkillCategoryType } from "../../types/skill";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import {
  useGetSkillByIdQuery,
  useAddSkillMutation,
  useEditSkillMutation,
} from "../../services/skill.service";
import { useGetSkillCategoriesQuery } from "../../services/skill-category.service";
import ModalSkillCategoryForm from "./ModalSkillCategoryForm";
import { isManage360 } from "../../utils/helper";
import { BaseModal, StyledTextField, Button, SelectBase, SelectOption } from "../../components/global";

interface ModalSkillFormProps {
  open: boolean;
  onClose: () => void;
  data: Skill | null;
  /** Filter categories by type (from current Skills page edition) */
  categoryType?: SkillCategoryType;
}

const ModalSkillForm = ({ open, onClose, data, categoryType }: ModalSkillFormProps) => {
  const [formModal, setFormModal] = React.useState<{
    open: boolean;
    data: SkillCategory | null;
  }>({
    open: false,
    data: null,
  });

  const { data: skill } = useGetSkillByIdQuery(data?.id!, {
    skip: true, //!data?.id,
  });
  const { data: categories, isLoading: loadingCategory } =
    useGetSkillCategoriesQuery({
      page: 0,
      pageSize: 100,
      sortField: "name",
      sortMode: "asc",
      ...(categoryType && { type: categoryType }),
    });

  const categoryOptions: SelectOption[] = React.useMemo(() => {
    if (!categories?.data) return [];
    return categories.data.map((cat) => ({
      label: cat.name,
      value: cat.id,
    }));
  }, [categories]);

  const [add, { isLoading: adding }] = useAddSkillMutation();
  const [edit, { isLoading: editing }] = useEditSkillMutation();

  const {
    handleSubmit,
    clearErrors,
    control,
    setValue,
    formState: { errors },
  } = useForm();

  const onSubmit = async (payload: any) => {
    try {
      if (!data) await add(payload).unwrap();
      else {
        payload.id = data?.id!;
        await edit(payload).unwrap();
      }
      onClose();
    } catch (err) {
      onClose();
    }
  };

  React.useEffect(() => {
    if (open) {
      setValue("name", data?.name || "");
      setValue("category", data?.category_id || null);
      setValue("description", data?.description || "");
      setValue("min_price", data?.min_price || "");
      setValue("max_price", data?.max_price || "");
      clearErrors();
    }
  }, [data, open, setValue, clearErrors]);

  React.useEffect(() => {
    if (skill) {
      setValue("name", skill?.name || "");
      setValue("category", skill?.category_id || null);
      setValue("description", skill?.description || "");
      setValue("min_price", skill?.min_price || "");
      setValue("max_price", skill?.max_price || "");
    }
  }, [skill, setValue]);

  return (
    <>
      <BaseModal
        open={open}
        onClose={onClose}
        title={`${!data ? "Create" : "Edit"} Skill`}
        maxWidth="xs"
        useGlassCard={false}
        footer={
          <>
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={adding || editing}
              onClick={handleSubmit(onSubmit)}
            >
              {!data ? "Create" : "Update"}
            </LoadingButton>
          </>
        }
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    label="Skill Name"
                    value={value || ""}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    inputRef={ref}
                    error={!!errors.name}
                    helperText={!!errors.name && "This field is required"}
                  />
                )}
              />
              <Controller
                name="category"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => {
                  const selectedOption = categoryOptions.find(
                    (opt) => opt.value === value
                  ) || null;
                  return (
                    <Box>
                      <SelectBase
                        options={categoryOptions}
                        value={selectedOption}
                        onChange={(newValue) => {
                          if (newValue && !Array.isArray(newValue)) {
                            onChange(newValue.value);
                          } else {
                            onChange(null);
                          }
                        }}
                        label="Skill Category"
                        placeholder="Select category..."
                        isLoading={loadingCategory}
                        error={!!errors.category}
                        helperText={
                          !!errors.category && "This field is required"
                        }
                        isDisabled={loadingCategory}
                      />
                      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => setFormModal({ data: null, open: true })}
                          startIcon={<AddIcon />}
                          sx={{
                            color: "text.secondary",
                            fontSize: "0.875rem",
                            "&:hover": {
                              backgroundColor: "rgba(18, 144, 60, 0.08)",
                              color: "#12903C",
                            },
                          }}
                        >
                          New Category
                        </Button>
                      </Stack>
                    </Box>
                  );
                }}
              />
              <Controller
                name="description"
                control={control}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <StyledTextField
                    label="Description"
                    value={value || ""}
                    fullWidth
                    onChange={onChange}
                    onBlur={onBlur}
                    inputRef={ref}
                    multiline
                    rows={3}
                    placeholder="Enter description (optional)"
                  />
                )}
              />
              {!isManage360() && (
                <Stack direction="row" spacing={1}>
                  <Controller
                    name="min_price"
                    control={control}
                    rules={{ required: true, min: 1 }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        label="Min Price ($)"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                        type="number"
                        error={!!errors.min_price}
                        helperText={
                          !!errors.min_price && "This field is required"
                        }
                      />
                    )}
                  />
                  <Controller
                    name="max_price"
                    control={control}
                    rules={{ required: true, min: 1 }}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <StyledTextField
                        label="Max Price ($)"
                        value={value || ""}
                        fullWidth
                        onChange={onChange}
                        onBlur={onBlur}
                        inputRef={ref}
                        type="number"
                        error={!!errors.max_price}
                        helperText={
                          !!errors.max_price && "This field is required"
                        }
                      />
                    )}
                  />
                </Stack>
              )}
            </Stack>
          </Box>
        </form>
      </BaseModal>
      <ModalSkillCategoryForm
        open={formModal.open}
        onClose={() => setFormModal({ open: false, data: null })}
        data={formModal.data}
      />
    </>
  );
};
export default ModalSkillForm;
